import { useState, useRef } from "react"
import { useRouter } from "next/router"
import Link from "next/link"
import cn from "classnames"
import { Swiper, SwiperSlide } from "swiper/react"
// Components
import Header from "@/components/newHeader/Header"
import Hero from "@/components/blog/Hero"
import Card from "@/components/blog/Card"
import Button from "@/components/newButton/Button"
import Subscribe from "@/components/blog/Subscribe"
import Questions from "@/components/blog/Questions"
import Footer from "@/components/newFooter"
import BasicHeadSeo from "@/components/basicHeadSeo"
// Utils
import { categorizePosts } from "@/components/blog/lib/utils"
import { getAllCategories, getAllPosts } from "@/components/blog/lib/cosmic"
import { ROBOTS_META } from "const"
import styles from "./index.module.scss"

interface SectionProps {
  title: string
  href?: string
  posts?: any[]
  mostPopularPosts?: any[]
  wrap?: boolean
  button?: boolean
  // eslint-disable-next-line no-unused-vars
  setQuery?: (query: string) => void
  router?: any
}

const Section = ({
  title,
  href,
  posts,
  mostPopularPosts,
  wrap,
  button,
  setQuery,
  router,
}: SectionProps) => {
  /* Swiper Handlers   */
  const ref = useRef(null)

  const [isBeginning, setIsBeginning] = useState<boolean>(true)
  const [isEnd, setIsEnd] = useState<boolean>(false)

  const goNext = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slideNext()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  const goPrev = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slidePrev()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  return (
    <div className={styles.section}>
      {href ? (
        <Link href={`/blog?category=${href}`} legacyBehavior>
          <h2 className={styles.pointer}>{title}</h2>
        </Link>
      ) : (
        <h2>{title}</h2>
      )}

      {!posts?.length ? (
        <>
          <div className={styles.empty}>
            <h3>No results at the moment</h3>

            <p>
              We upload articles all the time! Check back later to see if
              anything new pops up. Or read some related articles below!
            </p>
          </div>

          {mostPopularPosts?.length && (
            <>
              <Link href="/blog?category=most-popular-posts" legacyBehavior>
                <h2 className={styles.pointer}>Most popular posts</h2>
              </Link>

              <div className={styles.withoutResult}>
                {mostPopularPosts.map((p) => (
                  <Card
                    key={p.slug}
                    slug={p.slug}
                    title={p.title}
                    image={p.metadata?.image?.imgix_url}
                    readingTime={p.metadata?.reading_time}
                    details
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div
          className={cn({
            [styles.cards]: true,
            [styles.wrap]: wrap,
            [styles.noWrap]: !wrap,
          })}
        >
          <button
            className={cn({
              [styles.swiperButton]: true,
              [styles.swiperPrev]: !isBeginning,
              [styles.disabledPrev]: isBeginning,
            })}
            onClick={goPrev}
          />
          <button
            className={cn({
              [styles.swiperButton]: true,
              [styles.swiperNext]: !isEnd,
              [styles.disabledNext]: isEnd,
            })}
            onClick={goNext}
          />
          <div className={styles.scroll}>
            <div className={styles.cardWrapper}>
              <Swiper
                speed={1000}
                slidesPerView={4}
                initialSlide={0}
                autoplay={{
                  disableOnInteraction: true,
                }}
                grabCursor
                breakpoints={{
                  1020: {
                    slidesPerView: 4,
                    spaceBetween: 20,
                  },
                  860: {
                    slidesPerView: 3,
                    spaceBetween: 40,
                  },
                  540: {
                    slidesPerView: "auto",
                    spaceBetween: 20,
                  },
                  360: {
                    slidesPerView: "auto",
                    spaceBetween: 10,
                  },
                }}
                ref={ref}
              >
                {posts?.map((p, i) => (
                  <SwiperSlide key={i}>
                    <div className={styles.pair}>
                      <Card
                        key={p.slug}
                        slug={p.slug}
                        title={p.title}
                        image={p.metadata?.image?.imgix_url}
                        readingTime={p.metadata?.reading_time}
                        details
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      )}

      {button && (
        <Button
          theme={["short", "black"]}
          title="Return to all posts"
          altImg="Return to all posts"
          onClick={() => {
            router.push("/blog")
            setQuery("")
          }}
        />
      )}
    </div>
  )
}

export default function Blog({ posts, categories }) {
  const router = useRouter()
  const [query, setQuery] = useState("")

  const categorizedPosts = categorizePosts(posts)

  const search = Array.isArray(router?.query?.search)
    ? router?.query?.search[0]
    : router?.query?.search?.toLocaleLowerCase() || ""

  let content
  if (router?.query?.search) {
    const filteredPosts = posts?.filter(
      (p) =>
        p.content?.toLowerCase().includes(search) ||
        p.title?.toLowerCase().includes(search)
    )
    const mostPopularPosts = !filteredPosts?.length
      ? categorizedPosts["most-popular-posts"]
      : null

    content = (
      <Section
        title="Search results"
        posts={filteredPosts}
        mostPopularPosts={mostPopularPosts}
        wrap
        button
        // Only needed with button
        setQuery={setQuery}
        router={router}
      />
    )
  } else if (router?.query?.category) {
    content = (
      <Section
        title={
          categories?.find((c) => c.slug === router?.query?.category)?.title ||
          "Search results"
        }
        posts={categorizedPosts[router?.query?.category]}
        wrap
        button
        // Only needed with button
        setQuery={setQuery}
        router={router}
      />
    )
  } else {
    content = (
      <>
        {categories?.map((c) =>
          categorizedPosts[c.slug]?.length ? (
            <Section
              key={c.slug}
              title={c.title}
              href={c.slug}
              posts={categorizedPosts[c.slug]?.slice(0, 6)}
            />
          ) : null
        )}
      </>
    )
  }

  return (
    <>
      <BasicHeadSeo
        title="The Daily Dog - MawooPets.com"
        description="Find all you need to know as a new dog parent. Learn the best food, toys, treats, training, grooming, health, and genetics tips for your forever companion."
        url={process.env.NEXT_PUBLIC_ROOT_URL + "/blog"}
        image="https://www.mawoopets.com/images/mawoo-social.jpg"
        content={ROBOTS_META}
      />
      <main className={styles.main}>
        <Header />
        <Hero query={query} setQuery={setQuery} />
        {content}
        <Subscribe />
        <Questions />
        <Footer />
      </main>
    </>
  )
}

export async function getStaticProps() {
  const posts = await getAllPosts()
  const categories = await getAllCategories()

  return {
    props: {
      posts,
      categories,
    },
    revalidate: 72 * 60 * 60, // Revalidate 3 days
  }
}
